import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceAvailability } from '../../contexts/ServiceAvailabilityContext';
import { availabilityTitle, ServiceAvailabilityDetails, ServiceAvailabilityStatusLink } from './ServiceAvailabilityContent';

export default function ServiceAvailabilityNotice() {
  const value = useServiceAvailability();
  const { t, i18n } = useTranslation();
  const element = useRef(null);
  const title = availabilityTitle(value);
  const visible = Boolean(title);
  // App renders this notice outside its unified startup panel, in document flow.
  // Move the existing fixed header below it so both remain readable on mobile;
  // preserve the app/form instances throughout failures and recovery.
  useLayoutEffect(() => {
    const update = () => document.documentElement.style.setProperty('--service-availability-height', `${element.current?.getBoundingClientRect().height || 0}px`);
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    if (element.current) observer?.observe(element.current);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); document.documentElement.style.removeProperty('--service-availability-height'); };
  }, [visible, value?.maintenance, i18n.resolvedLanguage]);
  if (!visible) return null;
  const { maintenance, checking, retry } = value;
  return (
    <aside ref={element} role="status" aria-live="polite" className="sticky top-0 z-40 border-b border-amber-300 bg-amber-50 p-3 text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <div className="mx-auto flex max-w-6xl flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-64 break-words">
          <p className="font-semibold">{t(title)}</p>
          <ServiceAvailabilityDetails maintenance={maintenance} title={title} />
          {!maintenance && <p className="text-sm">{t('availability.preserveInput')}</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={retry} disabled={checking} className="rounded border border-current px-3 py-1.5 font-medium disabled:opacity-50">{t(checking ? 'availability.checking' : 'availability.retry')}</button>
          <ServiceAvailabilityStatusLink />
        </div>
      </div>
    </aside>
  );
}
